import { useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchTransactionStatus, isProjectTransactionInProgress } from "@/api/api";
import { useAxios } from "@/contexts/AxiosContext";
import type { ProjectTransactionNotification } from "@/types/api";
import { isTransactionPolledLocally } from "@/services/transactionPollOwnership";
import useVisibilityAwarePoll from "@/hooks/useVisibilityAwarePoll";
import { DEFAULT_TRANSACTION_INTERVAL_MS } from "@/utils/constants";

function inProgressMediaGenerationIds(items: readonly ProjectTransactionNotification[]): readonly string[] {
  return items.filter((item) => isProjectTransactionInProgress(item.transaction_status)).map((item) => item.transaction_id);
}

/**
 * Polls every in-progress image/video job in the notifications menu, including untagged ones such as
 * Style DNA tests. Each poll advances provider processing, so a job whose panel was closed finishes.
 */
const usePollInProgressGenerationTransactions = (
  projectId: string | undefined,
  items: readonly ProjectTransactionNotification[],
  options?: { readonly enabled?: boolean }
): void => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  const inProgressIds = useMemo(() => inProgressMediaGenerationIds(items), [items]);
  const inProgressIdsRef = useRef(inProgressIds);
  inProgressIdsRef.current = inProgressIds;

  const pollOnce = useCallback(async (): Promise<void> => {
    let shouldRefreshNotifications = false;

    await Promise.all(
      inProgressIdsRef.current.map(async (transactionId) => {
        if (isTransactionPolledLocally(transactionId)) {
          return;
        }

        try {
          const status = await fetchTransactionStatus(axiosClient, transactionId);
          if (status.status === "completed" || status.status === "error") {
            shouldRefreshNotifications = true;
          }
        } catch {
          // Ignore transient poll errors; next interval retries.
        }
      })
    );

    if (shouldRefreshNotifications) {
      await queryClient.invalidateQueries({ queryKey: ["projectTransactionNotifications", projectId] });
    }
  }, [axiosClient, projectId, queryClient]);

  const enabled = options?.enabled !== false && Boolean(projectId) && inProgressIds.length > 0;

  // criticalWhileHidden: these are image/video generation jobs, some Gemini-backed - polling is
  // what advances a Gemini job server-side (see startVisibilityAwarePoller), so this must never
  // pause on a hidden tab or an in-progress generation would silently stall.
  useVisibilityAwarePoll(pollOnce, DEFAULT_TRANSACTION_INTERVAL_MS, { enabled, criticalWhileHidden: true, runImmediately: true });
};

export default usePollInProgressGenerationTransactions;
